import React, { useEffect, useState } from 'react';
import axios from '../api/axios';
import assetUrl from '../api/assetUrl';
import { toFormData } from '../api/formData';
import MultilingualField from '../components/MultilingualField';
import IconPicker, { getServiceIcon } from '../components/IconPicker';
import { useNotify, errorMessage } from '../components/Toast';
import { IconEdit, IconTrash } from '../components/Icons';
import '../components/AdminUI.css';

export default function Content() {
  const [tab, setTab] = useState('opticians');
  return (
    <div className="admin-page">
      <div className="admin-tabs">
        <button className={`admin-tab ${tab === 'opticians' ? 'active' : ''}`} onClick={() => setTab('opticians')}>Opticien(s)</button>
        <button className={`admin-tab ${tab === 'testimonials' ? 'active' : ''}`} onClick={() => setTab('testimonials')}>Témoignages</button>
        <button className={`admin-tab ${tab === 'services' ? 'active' : ''}`} onClick={() => setTab('services')}>Services (homepage)</button>
        <button className={`admin-tab ${tab === 'photos' ? 'active' : ''}`} onClick={() => setTab('photos')}>Photos magasin</button>
      </div>
      {tab === 'opticians' && <Opticians />}
      {tab === 'testimonials' && <Testimonials />}
      {tab === 'services' && <ServicesList />}
      {tab === 'photos' && <StorePhotos />}
    </div>
  );
}

function Opticians() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const notify = useNotify();
  const load = () => axios.get('/opticians').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    try {
      const payload = { ...editing };
      if (payload.photo_file) delete payload.photo;
      const fd = toFormData(payload);
      if (editing.id) await axios.post(`/opticians/${editing.id}`, fd);
      else await axios.post('/opticians', fd);
      notify.success(editing.id ? 'Opticien mis à jour' : 'Opticien créé');
      setEditing(null);
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Erreur lors de l'enregistrement"));
    }
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try { await axios.delete(`/opticians/${id}`); notify.success('Supprimé'); load(); }
    catch (err) { notify.error(errorMessage(err, 'Erreur lors de la suppression')); }
  };

  return (
    <>
      {!editing && (
        <div className="admin-toolbar" style={{ justifyContent: 'flex-end' }}>
          <button className="admin-btn admin-btn-gold" onClick={() => setEditing({ name: '', i18n: {}, is_primary: false, sort_order: 0 })}>
            + Nouvel opticien
          </button>
        </div>
      )}
      {editing && (
        <div className="admin-form-card">
          <div className="admin-form-header">
            <h3>{editing.id ? "Modifier l'opticien" : 'Nouvel opticien'}</h3>
          </div>
          <div className="admin-form-body">
            <div className="admin-form-section">
              <div className="admin-form-section-title">Informations</div>
              <div className="admin-grid admin-grid-2">
                <div><label className="admin-label">Nom complet</label><input className="admin-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
                <div>
                  <label className="admin-label">Photo</label>
                  <input type="file" accept="image/*" onChange={(e) => setEditing({ ...editing, photo_file: e.target.files?.[0] })} />
                  {editing.photo && <img src={assetUrl(editing.photo)} alt="" style={{ height: 60, marginTop: 8, borderRadius: 4, border: '1px solid #e5e7eb' }} />}
                </div>
                <div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: '#475569' }}>
                    <input type="checkbox" checked={!!editing.is_primary} onChange={(e) => setEditing({ ...editing, is_primary: e.target.checked })} /> Opticien principal
                  </label>
                </div>
              </div>
            </div>
            <MultilingualField label="Traductions" value={editing.i18n} fields={['title', 'specialty', 'quote', 'bio']} multiline={['quote', 'bio']} onChange={(v) => setEditing({ ...editing, i18n: v })} />
          </div>
          <div className="admin-form-actions">
            <button className="admin-btn admin-btn-outline" onClick={() => setEditing(null)}>Annuler</button>
            <button className="admin-btn admin-btn-gold" onClick={save}>Enregistrer</button>
          </div>
        </div>
      )}
      <div className="admin-grid admin-grid-3">
        {items.map((o) => (
          <div key={o.id} className="admin-card" style={{ padding: 12 }}>
            {o.photo && <img src={assetUrl(o.photo)} alt={o.name} style={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 4 }} />}
            <div style={{ marginTop: 8, fontWeight: 600 }}>{o.name}</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>{o.i18n?.fr?.specialty || ''} {o.is_primary && '· Principal'}</div>
            <div className="admin-actions" style={{ marginTop: 10 }}>
              <button className="admin-icon-btn" title="Modifier" aria-label="Modifier" onClick={() => setEditing(o)}>
                <IconEdit />
              </button>
              <button className="admin-icon-btn is-danger" title="Supprimer" aria-label="Supprimer" onClick={() => remove(o.id)}>
                <IconTrash />
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}

function Testimonials() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => axios.get('/testimonials').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (editing.id) await axios.put(`/testimonials/${editing.id}`, editing);
    else await axios.post('/testimonials', editing);
    setEditing(null);
    load();
  };
  const remove = async (id) => { if (window.confirm('Supprimer ?')) { await axios.delete(`/testimonials/${id}`); load(); } };

  return (
    <>
      {!editing && (
        <div className="admin-toolbar" style={{ justifyContent: 'flex-end' }}>
          <button className="admin-btn admin-btn-gold" onClick={() => setEditing({ author_name: '', rating: 5, i18n: {}, is_active: true, sort_order: 0 })}>
            + Nouveau témoignage
          </button>
        </div>
      )}
      {editing && (
        <div className="admin-form-card">
          <div className="admin-form-header">
            <h3>{editing.id ? 'Modifier le témoignage' : 'Nouveau témoignage'}</h3>
          </div>
          <div className="admin-form-body">
            <div className="admin-form-section">
              <div className="admin-form-section-title">Informations</div>
              <div className="admin-grid admin-grid-2">
                <div><label className="admin-label">Auteur</label><input className="admin-input" value={editing.author_name} onChange={(e) => setEditing({ ...editing, author_name: e.target.value })} /></div>
                <div><label className="admin-label">Note (1-5)</label><input className="admin-input" type="number" min="1" max="5" value={editing.rating} onChange={(e) => setEditing({ ...editing, rating: parseInt(e.target.value || 5) })} /></div>
                <div><label className="admin-label">Avatar (URL)</label><input className="admin-input" value={editing.author_avatar || ''} onChange={(e) => setEditing({ ...editing, author_avatar: e.target.value })} /></div>
                <div>
                  <label style={{ display: 'inline-flex', alignItems: 'center', gap: 8, fontSize: 13.5, color: '#475569' }}>
                    <input type="checkbox" checked={!!editing.is_active} onChange={(e) => setEditing({ ...editing, is_active: e.target.checked })} /> Actif
                  </label>
                </div>
              </div>
            </div>
            <MultilingualField label="Témoignage" value={editing.i18n} fields={['text']} multiline={['text']} onChange={(v) => setEditing({ ...editing, i18n: v })} />
          </div>
          <div className="admin-form-actions">
            <button className="admin-btn admin-btn-outline" onClick={() => setEditing(null)}>Annuler</button>
            <button className="admin-btn admin-btn-gold" onClick={save}>Enregistrer</button>
          </div>
        </div>
      )}
      <div className="admin-card" style={{ padding: 0, overflow: 'auto' }}>
        <table className="admin-table">
          <thead><tr><th>Auteur</th><th>Note</th><th>Texte</th><th>Actif</th><th></th></tr></thead>
          <tbody>
            {items.map((t) => (
              <tr key={t.id}>
                <td>{t.author_name}</td><td>{'★'.repeat(t.rating)}</td>
                <td style={{ maxWidth: 360 }}>{t.i18n?.fr?.text}</td>
                <td><span className={`status-badge ${t.is_active ? 'active' : 'inactive'}`}>{t.is_active ? 'Actif' : 'Inactif'}</span></td>
                <td>
                  <div className="admin-actions">
                    <button className="admin-icon-btn" title="Modifier" aria-label="Modifier" onClick={() => setEditing(t)}>
                      <IconEdit />
                    </button>
                    <button className="admin-icon-btn is-danger" title="Supprimer" aria-label="Supprimer" onClick={() => remove(t.id)}>
                      <IconTrash />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function ServicesList() {
  const [items, setItems] = useState([]);
  const [editing, setEditing] = useState(null);
  const load = () => axios.get('/services').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const save = async () => {
    if (editing.id) await axios.put(`/services/${editing.id}`, editing);
    else await axios.post('/services', editing);
    setEditing(null);
    load();
  };
  const remove = async (id) => { if (window.confirm('Supprimer ?')) { await axios.delete(`/services/${id}`); load(); } };

  return (
    <>
      {!editing && (
        <div className="admin-toolbar" style={{ justifyContent: 'flex-end' }}>
          <button className="admin-btn admin-btn-gold" onClick={() => setEditing({ slug: '', name: '', icon: '', i18n: {}, is_active: true, sort_order: 0 })}>
            + Nouveau service
          </button>
        </div>
      )}
      {editing && (
        <div className="admin-form-card">
          <div className="admin-form-header">
            <h3>{editing.id ? 'Modifier le service' : 'Nouveau service'}</h3>
          </div>
          <div className="admin-form-body">
            <div className="admin-form-section">
              <div className="admin-form-section-title">Informations</div>
              <div className="admin-grid admin-grid-2">
                <div><label className="admin-label">Slug</label><input className="admin-input" value={editing.slug} onChange={(e) => setEditing({ ...editing, slug: e.target.value })} /></div>
                <div><label className="admin-label">Nom (FR)</label><input className="admin-input" value={editing.name} onChange={(e) => setEditing({ ...editing, name: e.target.value })} /></div>
                <div><label className="admin-label">Ordre</label><input className="admin-input" type="number" value={editing.sort_order} onChange={(e) => setEditing({ ...editing, sort_order: parseInt(e.target.value || 0) })} /></div>
              </div>
            </div>
            <div className="admin-form-section">
              <div className="admin-form-section-title">Icône</div>
              <IconPicker value={editing.icon || ''} onChange={(key) => setEditing({ ...editing, icon: key })} />
            </div>
            <MultilingualField label="Traductions" value={editing.i18n} fields={['name', 'description']} multiline={['description']} onChange={(v) => setEditing({ ...editing, i18n: v })} />
          </div>
          <div className="admin-form-actions">
            <button className="admin-btn admin-btn-outline" onClick={() => setEditing(null)}>Annuler</button>
            <button className="admin-btn admin-btn-gold" onClick={save}>Enregistrer</button>
          </div>
        </div>
      )}
      <div className="admin-card" style={{ padding: 0, overflow: 'auto' }}>
        <table className="admin-table">
          <thead><tr><th>Slug</th><th>Nom</th><th>Icône</th><th>Actif</th><th></th></tr></thead>
          <tbody>
            {items.map((s) => {
              const iconDef = getServiceIcon(s.icon);
              const IconCmp = iconDef?.Icon;
              return (
                <tr key={s.id}>
                  <td>{s.slug}</td>
                  <td>{s.name}</td>
                  <td>
                    {IconCmp ? (
                      <span className="icon-cell"><IconCmp /> {iconDef.label}</span>
                    ) : (
                      <span style={{ color: '#94a3b8', fontSize: 12 }}>—</span>
                    )}
                  </td>
                  <td><span className={`status-badge ${s.is_active ? 'active' : 'inactive'}`}>{s.is_active ? 'Actif' : 'Inactif'}</span></td>
                  <td>
                    <div className="admin-actions">
                      <button className="admin-icon-btn" title="Modifier" aria-label="Modifier" onClick={() => setEditing(s)}>
                        <IconEdit />
                      </button>
                      <button className="admin-icon-btn is-danger" title="Supprimer" aria-label="Supprimer" onClick={() => remove(s.id)}>
                        <IconTrash />
                      </button>
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </>
  );
}

function StorePhotos() {
  const [items, setItems] = useState([]);
  const [draft, setDraft] = useState({ image: null, sort_order: 0, is_active: true });
  const notify = useNotify();
  const load = () => axios.get('/store-photos').then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);
  const upload = async () => {
    if (!draft.image) {
      notify.error('Veuillez sélectionner une image.');
      return;
    }
    try {
      const fd = toFormData(draft);
      await axios.post('/store-photos', fd);
      notify.success('Photo ajoutée');
      setDraft({ image: null, sort_order: 0, is_active: true });
      load();
    } catch (err) {
      notify.error(errorMessage(err, "Erreur lors de l'envoi de la photo"));
    }
  };
  const remove = async (id) => {
    if (!window.confirm('Supprimer ?')) return;
    try { await axios.delete(`/store-photos/${id}`); notify.success('Supprimée'); load(); }
    catch (err) { notify.error(errorMessage(err, 'Erreur lors de la suppression')); }
  };

  return (
    <>
      <div className="admin-toolbar">
        <input type="file" accept="image/*" onChange={(e) => setDraft({ ...draft, image: e.target.files?.[0] })} />
        <input className="admin-input" type="number" placeholder="Ordre" value={draft.sort_order} onChange={(e) => setDraft({ ...draft, sort_order: parseInt(e.target.value || 0) })} style={{ maxWidth: 120 }} />
        <button className="admin-btn admin-btn-gold" style={{ marginLeft: 'auto' }} onClick={upload}>Téléverser</button>
      </div>
      <div className="admin-grid admin-grid-3">
        {items.map((p) => (
          <div key={p.id} className="admin-card" style={{ padding: 8 }}>
            <img src={assetUrl(p.image_path)} alt="" style={{ width: '100%', height: 140, objectFit: 'cover', borderRadius: 4 }} />
            <div style={{ marginTop: 8, display: 'flex', alignItems: 'center', gap: 6 }}>
              <span style={{ fontSize: 12, color: '#6b7280' }}>ordre {p.sort_order}</span>
              <button className="admin-icon-btn is-danger" style={{ marginLeft: 'auto' }} title="Supprimer" aria-label="Supprimer" onClick={() => remove(p.id)}>
                <IconTrash />
              </button>
            </div>
          </div>
        ))}
      </div>
    </>
  );
}
